/**
 * RL Loop Dashboard
 *
 * Main dashboard for the RL (Reinforcement Learning) Loop feature.
 * Provides interface for creating, monitoring, and managing automated prompt improvement loops.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Download as ExportIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Autorenew as AutoResumeIcon,
  ContentCopy as CloneIcon
} from '@mui/icons-material';
import RLLoopConfigForm from './RLLoopConfigForm';
import RLLoopMonitor from './RLLoopMonitor';
import RLLoopExportModal from './RLLoopExportModal';
import { listRLLoops, deleteRLLoop, getRLLoopStatus } from '../../../services/rlLoopApi';

const STATUS_COLORS = {
  pending: 'default',
  running: 'primary',
  paused_by_user: 'warning',
  paused_for_code_change: 'warning',
  completed: 'success',
  failed: 'error',
  cancelled: 'default'
};

const STATUS_LABELS = {
  pending: 'Pending',
  running: 'Running',
  paused_by_user: 'Paused',
  paused_for_code_change: 'Paused (Code)',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled'
};

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

function RLLoopDashboard({ token, isResearcher = false }) {
  const [activeTab, setActiveTab] = useState(0);
  const [loops, setLoops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Selected loop for viewing
  const [selectedLoopId, setSelectedLoopId] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  // Create dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Export modal
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportLoop, setExportLoop] = useState(null);

  // Clone state
  const [cloneConfig, setCloneConfig] = useState(null);
  const [cloneSourceName, setCloneSourceName] = useState('');

  // Fetch loops
  const fetchLoops = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {
        page: page + 1,
        limit: rowsPerPage
      };
      if (statusFilter) filters.status = statusFilter;

      const result = await listRLLoops(filters, token);
      setLoops(result.loops || []);
      setTotal(result.pagination?.total || 0);
      setError(null);
    } catch (err) {
      setError(err.error || 'Failed to load loops');
    } finally {
      setLoading(false);
    }
  }, [token, page, rowsPerPage, statusFilter]);

  useEffect(() => {
    fetchLoops();
  }, [fetchLoops]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleViewLoop = (loopId) => {
    setSelectedLoopId(loopId);
    setViewDialogOpen(true);
  };

  const handleDeleteLoop = async (loopId) => {
    if (window.confirm('Are you sure you want to delete this loop? This cannot be undone.')) {
      try {
        await deleteRLLoop(loopId, token);
        fetchLoops();
      } catch (err) {
        setError(err.error || 'Failed to delete loop');
      }
    }
  };

  const handleExportLoop = (loop) => {
    setExportLoop(loop);
    setExportModalOpen(true);
  };

  const handleCloneLoop = async (loopId) => {
    try {
      const statusRes = await getRLLoopStatus(loopId, token);
      setCloneConfig(statusRes.config);
      setCloneSourceName(statusRes.name || '');
      setCreateDialogOpen(true);
    } catch (err) {
      setError(err.error || 'Failed to fetch loop config for cloning');
    }
  };

  const handleCloneFromMonitor = (config, name) => {
    setViewDialogOpen(false);
    setCloneConfig(config);
    setCloneSourceName(name || '');
    setCreateDialogOpen(true);
  };

  const handleLoopStarted = (result) => {
    setCreateDialogOpen(false);
    setSelectedLoopId(result._id);
    setViewDialogOpen(true);
    fetchLoops();
  };

  const filteredLoops = loops.filter(loop =>
    !searchQuery || loop.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Count active loops
  const activeLoopCount = loops.filter(l => l.status === 'running').length;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h5">RL Loop Manager</Typography>
          <Typography variant="body2" color="text.secondary">
            Automated iterative improvement for therapist prompts
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {activeLoopCount > 0 && (
            <Chip
              label={`${activeLoopCount} Active`}
              color="primary"
              variant="outlined"
            />
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            New Loop
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="All Loops" />
            <Tab label={`Active (${activeLoopCount})`} />
            <Tab label="Completed" />
          </Tabs>
        </Box>

        <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search loops..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
          <Tooltip title="Refresh">
            <IconButton onClick={fetchLoops}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <TabPanel value={activeTab} index={0}>
          <LoopTable
            loops={filteredLoops}
            loading={loading}
            onView={handleViewLoop}
            onDelete={handleDeleteLoop}
            onExport={handleExportLoop}
            onClone={handleCloneLoop}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <LoopTable
            loops={filteredLoops.filter(l => ['running', 'paused_by_user', 'paused_for_code_change'].includes(l.status))}
            loading={loading}
            onView={handleViewLoop}
            onDelete={handleDeleteLoop}
            onExport={handleExportLoop}
            onClone={handleCloneLoop}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <LoopTable
            loops={filteredLoops.filter(l => l.status === 'completed')}
            loading={loading}
            onView={handleViewLoop}
            onDelete={handleDeleteLoop}
            onExport={handleExportLoop}
            onClone={handleCloneLoop}
          />
        </TabPanel>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Paper>

      {/* Create / Clone Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
          setCloneConfig(null);
          setCloneSourceName('');
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{cloneConfig ? 'Clone RL Loop' : 'Create New RL Loop'}</DialogTitle>
        <DialogContent>
          <RLLoopConfigForm
            token={token}
            onLoopStarted={(result) => {
              setCloneConfig(null);
              setCloneSourceName('');
              handleLoopStarted(result);
            }}
            isResearcher={isResearcher}
            initialConfig={cloneConfig}
            initialName={cloneSourceName}
          />
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent>
          {selectedLoopId && (
            <RLLoopMonitor
              loopId={selectedLoopId}
              token={token}
              onClose={() => setViewDialogOpen(false)}
              onClone={handleCloneFromMonitor}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Export Modal */}
      <RLLoopExportModal
        open={exportModalOpen}
        onClose={() => {
          setExportModalOpen(false);
          setExportLoop(null);
        }}
        loopId={exportLoop?._id}
        token={token}
        loop={exportLoop}
      />
    </Box>
  );
}

// Loops Table Component
function LoopTable({ loops, loading, onView, onDelete, onExport, onClone }) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loops.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', p: 4 }}>
        <Typography color="text.secondary">No loops found</Typography>
      </Box>
    );
  }

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Progress</TableCell>
            <TableCell>Quality</TableCell>
            <TableCell>Cost</TableCell>
            <TableCell>Created</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loops.map((loop) => (
            <TableRow key={loop._id} hover>
              <TableCell>
                <Typography variant="body2" fontWeight="medium">
                  {loop.name}
                </Typography>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Chip
                    label={STATUS_LABELS[loop.status] || loop.status}
                    color={STATUS_COLORS[loop.status] || 'default'}
                    size="small"
                  />
                  {loop.autoResumeCount > 0 && (
                    <Tooltip title={`Auto-resumed ${loop.autoResumeCount} time(s) after server restart`}>
                      <AutoResumeIcon fontSize="small" color="info" />
                    </Tooltip>
                  )}
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {loop.progress?.currentIteration || 0} / {loop.config?.maxIterations || '?'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {loop.completionPercentage || 0}%
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {loop.metrics?.currentQuality?.toFixed(1) || '—'}
                </Typography>
                {loop.metrics?.startingQuality && (
                  <Typography variant="caption" color="text.secondary">
                    from {loop.metrics.startingQuality.toFixed(1)}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                ${loop.costs?.totalCost?.toFixed(4) || '0'}
              </TableCell>
              <TableCell>
                {new Date(loop.createdAt).toLocaleDateString()}
              </TableCell>
              <TableCell>
                <Tooltip title="View Details">
                  <IconButton size="small" onClick={() => onView(loop._id)}>
                    <ViewIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Clone Configuration">
                  <IconButton size="small" onClick={() => onClone(loop._id)}>
                    <CloneIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Export">
                  <IconButton size="small" onClick={() => onExport(loop)}>
                    <ExportIcon />
                  </IconButton>
                </Tooltip>
                {!['running'].includes(loop.status) && (
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => onDelete(loop._id)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default RLLoopDashboard;
